import enviro from 'enviro';
import Raven from 'raven-js';
import { createContext, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useJobQueue } from '../../JobQueueProvider';
import { fetchAblyAuthToken } from '../../chat/api/chatApi';
import { selectAblyChannelId, selectActiveThreadId } from '../../chat/selectors/chatv2Selectors';
import { selectChatspotUserInfo } from '../../chatspotAuth/selectors/chatspotAuthSelectors';
import { useIsUngatedForCopilotV2 } from '../../gating/hooks/useIsUngatedForCopilotV2';
import { createEnhancedAblyClient } from '../AblyClient';
import { useGetSubscriptionHandler } from '../hooks/useGetSubscriptionHandler';

// This wrapper is used to consolidate all Ably-related channel subscription logic once we have established a connection to the Ably client
import { jsx as _jsx } from "react/jsx-runtime";
const AblyChannelSubscriptionsWrapper = ({
  ablyRealtimeClient,
  children
}) => {
  const {
    setIsAblyChannelConnected
  } = useJobQueue();
  const ablyChannelId = useSelector(selectAblyChannelId);
  const currentThreadId = useSelector(selectActiveThreadId);
  const subscriptionHandler = useGetSubscriptionHandler({
    threadId: currentThreadId || 0
  });
  useEffect(() => {
    // making this check here instead of AblyRealtimeClientProvider is important in preventing React reconciliation errors
    // conditionally rendering the wrappers can cause downstream rendering issues
    if (!currentThreadId || !ablyChannelId || !ablyRealtimeClient) {
      return;
    }
    const currentAblyChannel = ablyRealtimeClient.channels.get(ablyChannelId);
    currentAblyChannel.subscribe(subscriptionHandler);
    // indicate to job queue that the ably realtime client and channel are properly connected during initial setup
    setIsAblyChannelConnected(true);
    return () => {
      currentAblyChannel.unsubscribe(subscriptionHandler);
      currentAblyChannel.detach();
    };
  }, [ablyChannelId, ablyRealtimeClient, currentThreadId, subscriptionHandler, setIsAblyChannelConnected]);
  return children;
};

// We use type assertion to tell TS that the context value will ALWAYS be defined
// See below where we're doing null checks for the ablyRealtimeClient value before using it
export const AblyRealtimeClientContext = /*#__PURE__*/createContext(null);

// Default values for disconnected/suspended retry are 15s and 30s
// Add 10s variation to avoid client reconnects storming the ably servers
// i.e. prevents clients from all trying to reconnect at the same time when there's a complete outage
const variation = Math.floor(Math.random() * 10) + 1;
const disconnectedRetryTimeout = (10 + variation) * 1000; //  10-20 seconds
const suspendedRetryTimeout = (20 + variation) * 1000; // 20-30 seconds

const getClientOptions = () => {
  const hublet = enviro.getHublet();
  return {
    autoConnect: true,
    queryTime: true,
    disconnectedRetryTimeout,
    suspendedRetryTimeout,
    environment: `hubspot-${hublet}`,
    fallbackHosts: [`hubspot-${hublet}-a-fallback.ably-realtime.com`, `hubspot-${hublet}-b-fallback.ably-realtime.com`, `hubspot-${hublet}-c-fallback.ably-realtime.com`, `hubspot-${hublet}-d-fallback.ably-realtime.com`, `hubspot-${hublet}-e-fallback.ably-realtime.com`]
  };
};
const AblyRealtimeClientProvider = ({
  children
}) => {
  const isUngatedForCopilotV2 = useIsUngatedForCopilotV2();
  const userInfo = useSelector(selectChatspotUserInfo);
  const currentThreadId = useSelector(selectActiveThreadId);

  // Keep track of previous client for cleanup
  const previousClientRef = useRef(null);

  // this instantiates both an Ably client + an Ably SDK
  const ablyRealtimeClient = useMemo(() => {
    // Ably auth requires a user id and a thread id
    if (!isUngatedForCopilotV2 || !(userInfo !== null && userInfo !== void 0 && userInfo.id) || !currentThreadId) {
      // Clean up existing client if conditions are no longer met
      if (previousClientRef.current) {
        const oldClient = previousClientRef.current;
        oldClient.close();
        previousClientRef.current = null;
      }
      return null;
    }
    if (previousClientRef.current) {
      const oldClient = previousClientRef.current;
      oldClient.close();
      previousClientRef.current = null;
    }

    // TODO: configure event logging
    try {
      const newClient = createEnhancedAblyClient(Object.assign({
        useTokenAuth: true,
        authCallback: (tokenParams, callback) => {
          // Tokens expire after 1 hour. authCallback will automatically request a new token
          // before the current one expires, ensuring a continuous connection to Ably.
          fetchAblyAuthToken({
            clientId: userInfo.id.toString(),
            threadId: currentThreadId
          }).then(tokenRequest => {
            callback(null, tokenRequest);
          }).catch(err => {
            Raven.captureException(err, {
              extra: {
                component: 'AblyRealtimeClientProvider',
                action: 'fetchAblyAuthToken',
                context: 'Ably auth token fetch failed'
              }
            });
            callback(err, null);
          });
        }
      }, getClientOptions()));

      // Store reference for cleanup
      previousClientRef.current = newClient;
      return newClient;
    } catch (e) {
      // TODO: handle failure and add retry logic for getting new auth token
      Raven.captureException(e instanceof Error ? e : new Error(String(e)), {
        extra: {
          component: 'AblyRealtimeClientProvider',
          action: 'createEnhancedAblyClient',
          context: 'Error creating Ably client'
        }
      });
      return null;
    }
  }, [isUngatedForCopilotV2, userInfo, currentThreadId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (previousClientRef.current) {
        previousClientRef.current.close();
        previousClientRef.current = null; // Reset on unmount
      }
    };
  }, []);
  return /*#__PURE__*/_jsx(AblyRealtimeClientContext.Provider, {
    value: ablyRealtimeClient,
    children: /*#__PURE__*/_jsx(AblyChannelSubscriptionsWrapper, {
      ablyRealtimeClient: ablyRealtimeClient,
      children: children
    })
  });
};
export default AblyRealtimeClientProvider;